import { useCallback, useEffect, useRef, useState } from "react";
import { useUrlState } from "@/lib/useUrlState";
import { Loader2, RefreshCw, Plus, AlertTriangle, CalendarClock, Truck, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { opsApi, money, pct } from "../../lib/api";
import PayAppDetail from "./PayAppDetail";
import BillingProfile from "./BillingProfile";

const STATUS_TONE = {
  draft: "border-slate-200 bg-slate-50 text-slate-600",
  submitted: "border-sky-200 bg-sky-50 text-sky-700",
  approved: "border-emerald-200 bg-emerald-50 text-emerald-700",
  paid: "border-emerald-200 bg-emerald-50 text-emerald-700",
  rejected: "border-rose-200 bg-rose-50 text-rose-700",
  superseded: "border-slate-200 bg-white text-slate-400",
};

/** OPS-6. The schedule of values always equals the revised contract; the pay applications are
 *  a G702/G703 to the cent. Accounting still keys it into the GC's portal — everything on our
 *  side of that is prepared for them. */
export default function InvoicingTab({ project, reload }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useUrlState("payapp", "");
  const wasOpen = useRef(false);
  useEffect(() => { if (wasOpen.current && !open) load(); wasOpen.current = !!open; }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const [busy, setBusy] = useState(false);
  const [showSov, setShowSov] = useState(false);
  const [view, setView] = useState("billing");

  const load = useCallback(async () => {
    try { setData(await opsApi.invoicing(project.id)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not load the billing"); }
  }, [project.id]);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setBusy(true);
    try {
      setData(await opsApi.generateSov(project.id));
      toast.success("Schedule of values generated from the budget");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not generate the SOV"); }
    finally { setBusy(false); }
  };

  const draft = async (kind) => {
    setBusy(true);
    try {
      const app = await opsApi.createPayApp(project.id, { kind, from_suggestion: true });
      toast.success(`${app.number} drafted — review the suggested amounts`);
      setOpen(app.id);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not draft that application"); }
    finally { setBusy(false); }
  };

  if (!data) {
    return <div className="flex items-center justify-center gap-2 py-16 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading billing…
    </div>;
  }
  if (open) {
    return <PayAppDetail project={project} appId={open} onBack={() => setOpen("")}
                         onChanged={() => { load(); reload?.(); }} />;
  }

  const sov = data.sov;
  const s = data.summary;
  const rem = data.reminder;
  const existingDraft = data.applications.find((a) => a.editable);

  return (
    <div className="space-y-4" data-testid="ops-invoicing-tab">
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1 rounded-lg bg-slate-100 p-1">
        {[["billing", "Pay Applications"], ["setup", "Billing Setup"]].map(([k, label]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`ops-invoicing-view-${k}`}
                  className={`rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${
                    view === k ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"}`}>
            {label}
          </button>
        ))}
        </div>
        <Button size="sm" variant="outline" data-testid="ops-invoicing-open-billing-dashboard"
                onClick={() => window.dispatchEvent(
                  new CustomEvent("abs:navigate", { detail: { tab: "billing" } }))}>
          Billing dashboard
        </Button>
      </div>

      {view === "setup" && <BillingProfile project={project} />}
      {view === "billing" && (<>
      {rem?.due && (
        <div className={`flex flex-wrap items-center gap-3 rounded-lg border px-3 py-2 text-xs ${
          rem.tone === "bad" ? "border-rose-200 bg-rose-50 text-rose-800"
            : "border-amber-200 bg-amber-50 text-amber-800"}`}
             data-testid="ops-billing-reminder">
          <CalendarClock className="h-3.5 w-3.5" />
          <span className="font-medium">{rem.line}</span>
          <span className="text-slate-500">billing day {rem.billing_day}</span>
          {!existingDraft && sov.balanced && (
            <Button size="sm" className="ml-auto" disabled={busy} onClick={() => draft("progress")}
                    data-testid="ops-reminder-draft">
              Draft the pay app
            </Button>
          )}
        </div>
      )}

      {!sov.balanced && sov.exists && (
        <div className="flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800"
             data-testid="ops-sov-out-of-balance">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{sov.check} — no application can be prepared until it ties.</span>
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[["Revised contract", money(sov.revised_contract), "ops-inv-contract"],
          ["Billed to date", money(s.billed_to_date), "ops-inv-billed"],
          ["Retainage held", money(s.retainage_held), "ops-inv-retainage"],
          ["Awaiting payment", money(s.outstanding_value), "ops-inv-outstanding"],
          ["Paid to date", money(s.paid_to_date), "ops-inv-paid"]].map(([k, v, tid]) => (
          <div key={k} className="rounded-lg border border-slate-200 bg-white px-4 py-3"
               data-testid={tid}>
            <p className="text-xs text-slate-500">{k}</p>
            <p className="text-lg font-semibold text-slate-900">{v}</p>
          </div>
        ))}
      </div>

      <ShippedHandoff project={project} rows={data.shipped_pos || []} canEdit={data.can_edit} onChanged={load} />

      {/* ── the schedule of values ── */}
      <section className="rounded-xl border border-slate-200 bg-white" data-testid="ops-sov">
        <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-2.5">
          <h2 className="text-sm font-semibold text-slate-900">Schedule of Values</h2>
          {sov.exists && !sov.balanced && (
            <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] text-rose-700"
                  data-testid="ops-sov-balance">
              Out of balance
            </span>
          )}
          <span className="text-xs text-slate-500">
            {sov.base_line_count} cost code(s) · {sov.co_line_count} change order line(s)
          </span>
          <button onClick={() => setShowSov((v) => !v)} data-testid="ops-sov-toggle"
                  className="text-xs font-medium text-blue-600 hover:underline">
            {showSov ? "Hide" : "Show"} the lines
          </button>
          {data.can_edit && (
            <Button variant="outline" size="sm" className="ml-auto" disabled={busy}
                    onClick={generate} data-testid="ops-sov-generate">
              <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
              {sov.exists ? "Regenerate from the budget" : "Generate from the budget"}
            </Button>
          )}
        </div>
        <p className="px-4 py-2 text-xs text-slate-500" data-testid="ops-sov-check">{sov.check}</p>
        {showSov && (
          <div className="max-h-[50vh] overflow-auto border-t border-slate-100">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left">Item</th>
                  <th className="px-3 py-2 text-left">Description</th>
                  <th className="px-3 py-2 text-right">Scheduled Value</th>
                  <th className="px-3 py-2 text-right">Billed To Date</th>
                  <th className="px-3 py-2 text-right">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sov.lines.map((l) => (
                  <tr key={l.id} data-testid={`ops-sov-line-${l.cost_code}`}
                      className={l.kind === "co" ? "bg-violet-50/40" : ""}>
                    <td className="px-3 py-1.5 font-mono">{l.cost_code}</td>
                    <td className="px-3 py-1.5">
                      {l.cost_code_name}
                      {l.kind === "co" && (
                        <span className="ml-1 text-[10px] uppercase text-violet-700">
                          executed CO
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {money(l.scheduled_value)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">
                      {money(l.billed_to_date)}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums">{pct(l.pct_billed)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-slate-50 text-xs font-semibold">
                <tr>
                  <td colSpan={2} className="px-3 py-2">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums"
                      data-testid="ops-sov-total">{money(sov.scheduled_total)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </section>

      {/* ── the applications ── */}
      <section className="rounded-xl border border-slate-200 bg-white"
               data-testid="ops-pay-apps">
        <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 px-4 py-2.5">
          <h2 className="text-sm font-semibold text-slate-900">Applications For Payment</h2>
          <span className="text-xs text-slate-500">
            {sov.terms.retainage_percent ? `Retainage ${sov.terms.retainage_percent}%`
              : "No retainage on this contract"} ·
            {" "}{sov.terms.payment_terms_days ? `net ${sov.terms.payment_terms_days}`
              : "payment terms not set"} ·
            {" "}{sov.terms.billing_day ? `billing day ${sov.terms.billing_day}`
              : "billing day not set"}
          </span>
          {data.can_edit && (
            <div className="ml-auto flex gap-2">
              <Button variant="outline" size="sm" disabled={busy || !sov.balanced || !!existingDraft}
                      onClick={() => draft("retention_release")}
                      title={existingDraft ? "Finish the draft first" : "Release the retainage held"}
                      data-testid="ops-payapp-retention">
                Retention release
              </Button>
              <Button size="sm" disabled={busy || !sov.balanced || !!existingDraft}
                      onClick={() => draft("progress")} data-testid="ops-payapp-new"
                      title={existingDraft ? "There is already a draft application" : ""}>
                <Plus className="mr-1.5 h-3.5 w-3.5" /> Draft the next application
              </Button>
            </div>
          )}
        </div>
        {data.applications.length === 0 ? (
          <p className="px-4 py-8 text-center text-sm text-slate-500">
            No applications yet.{" "}
            {sov.exists
              ? "Draft the first one — it is suggested from the field's install progress, the "
                + "material received and not yet installed, and any executed change orders."
              : "Generate the schedule of values first."}
          </p>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Application</th>
                <th className="px-3 py-2 text-left">Type</th>
                <th className="px-3 py-2 text-left">Period Ending</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-right">Completed &amp; Stored</th>
                <th className="px-3 py-2 text-right">Retainage</th>
                <th className="px-3 py-2 text-right">Payment Due</th>
                <th className="px-3 py-2 text-left">Paid</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.applications.map((a) => (
                <tr key={a.id} onClick={() => setOpen(a.id)}
                    data-testid={`ops-payapp-row-${a.number}${a.revision > 1 ? `-r${a.revision}` : ""}`}
                    className="cursor-pointer hover:bg-slate-50">
                  <td className="px-3 py-2 font-medium text-slate-900">
                    {a.number}{a.revision > 1 ? ` R${a.revision}` : ""}
                  </td>
                  <td className="px-3 py-2">{a.kind_label}</td>
                  <td className="px-3 py-2">{a.period_end || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`rounded-full border px-2 py-0.5 ${STATUS_TONE[a.status]}`}>
                      {a.status_label}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {money(a.completed_and_stored)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(a.retainage_held)}</td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {money(a.payment_due)}
                  </td>
                  <td className="px-3 py-2">
                    {a.paid_at ? `${money(a.paid_amount)} · ${a.paid_at.slice(0, 10)}` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
      </>)}
    </div>
  );
}


/** Billing handoff — purchase orders that have shipped (or landed) and are not yet on a pay app. */
function ShippedHandoff({ project, rows, canEdit, onChanged }) {
  const [showBilled, setShowBilled] = useState(false);
  const open = rows.filter((r) => !r.billed);
  const list = showBilled ? rows : open;
  if (rows.length === 0) return null;
  const mark = async (r, clear = false) => {
    try {
      await opsApi.poBilled(project.id, r.id, { clear, billed_on: clear ? "" : (window.prompt(`Billed on which pay application? (e.g. PA-003)`, "") || "") });
      toast.success(clear ? `${r.po_number} back on the handoff list` : `${r.po_number} marked billed`);
      onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update that PO"); }
  };
  const openPo = (id) => window.dispatchEvent(new CustomEvent("abs:navigate", { detail: { path: `/jobs?job=${project.id}:purchasing&po=${id}` } }));
  return (
    <section className="rounded-xl border border-teal-200 bg-white" data-testid="ops-inv-shipped">
      <div className="flex flex-wrap items-center gap-2 border-b border-teal-100 bg-teal-50/60 px-4 py-2.5 text-xs">
        <Truck className="h-3.5 w-3.5 text-teal-700" />
        <span className="font-semibold text-slate-900">Shipped — ready to bill</span>
        <span className="text-slate-500">{open.length} PO(s) · {money(open.reduce((t, r) => t + r.total, 0))} shipped and not yet on a pay app</span>
        <button className="ml-auto text-slate-500 hover:text-slate-800" onClick={() => setShowBilled((v) => !v)} data-testid="ops-inv-shipped-toggle">
          {showBilled ? "Hide billed" : `Show billed (${rows.length - open.length})`}
        </button>
      </div>
      <table className="w-full text-xs">
        <tbody className="divide-y divide-slate-100">
          {list.map((r) => (
            <tr key={r.id} className={r.billed ? "text-slate-400" : ""} data-testid={`ops-inv-shipped-${r.po_number}`}>
              <td className="px-4 py-1.5 font-mono">{r.po_number}</td>
              <td className="px-2 py-1.5">{r.manufacturer}</td>
              <td className="px-2 py-1.5 text-slate-500">{r.scope_codes.join(" · ")}</td>
              <td className="px-2 py-1.5">{r.stage}{r.shipped_on ? ` · shipped ${r.shipped_on}` : ""}{r.received_at ? ` · received ${r.received_at}` : ""}</td>
              <td className="px-2 py-1.5 text-right font-semibold tabular-nums">{money(r.total)}</td>
              <td className="px-2 py-1.5 text-right">
                {r.billed ? (
                  <span>billed{r.billed_on ? ` on ${r.billed_on}` : ""}{canEdit && <button className="ml-2 text-[#007BFF] hover:underline" onClick={() => mark(r, true)} data-testid={`ops-inv-unbill-${r.po_number}`}>undo</button>}</span>
                ) : canEdit && (
                  <Button size="sm" variant="data" className="h-6 text-[11px]" onClick={() => mark(r)} data-testid={`ops-inv-bill-${r.po_number}`}>Mark billed</Button>
                )}
              </td>
              <td className="px-3 py-1.5">
                <button className="text-slate-400 hover:text-slate-800" title="Open the PO" onClick={() => openPo(r.id)} data-testid={`ops-inv-shipped-open-${r.po_number}`}>
                  <ExternalLink className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
